import React, { useState } from 'react';
import { useLiveRanking } from '../hooks/useLiveRanking';
import { rankingsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

function formatTime(ms) {
  if (!ms) return '—';
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}h${String(m).padStart(2,"0")}'${String(sec).padStart(2,"0")}"`;
}

function LiveDot({ connected }) {
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
      <span style={{ width:8, height:8, borderRadius:'50%', background:connected?'#4CAF50':'#8C7A6A', boxShadow:connected?'0 0 0 3px rgba(76,175,80,.2)':'none', animation:connected?'lp 2s infinite':'none', display:'inline-block' }} />
      <style>{`@keyframes lp{0%,100%{opacity:1}50%{opacity:.35}}`}</style>
    </span>
  );
}

function Podium({ rankings, color }) {
  if (!rankings.length) return null;
  const slots = [
    { idx:1, medal:'🥈', height:110, label:'2e' },
    { idx:0, medal:'🥇', height:152, label:'1er' },
    { idx:2, medal:'🥉', height:84,  label:'3e' },
  ];
  const bgs = [
    'linear-gradient(160deg,#7A8C88,#5A6B68)',
    `linear-gradient(160deg,${color},${color}bb)`,
    'linear-gradient(160deg,#C4A882,#A08060)',
  ];
  return (
    <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'center', gap:6, marginBottom:32, padding:'0 16px' }}>
      {slots.map(({ idx, medal, height, label }, i) => {
        const r = rankings[idx];
        if (!r) return <div key={i} style={{ flex:1 }} />;
        return (
          <div key={i} style={{ flex:1, maxWidth:160, display:'flex', flexDirection:'column', alignItems:'center' }}>
            <div style={{ textAlign:'center', marginBottom:6, padding:'0 4px' }}>
              <div style={{ fontSize:20 }}>{medal}</div>
              <div style={{ fontWeight:700, fontSize:12, color:'#1A1208', marginTop:2, lineHeight:1.2 }}>{r.teamName}</div>
              <div style={{ fontSize:10, color:'#8C7A6A', fontFamily:'Space Mono,monospace', marginTop:1 }}>
                {(r.members||[]).slice(0,2).join(', ')}{(r.members||[]).length>2?'…':''}
              </div>
            </div>
            <div style={{ width:'100%', height, borderRadius:'6px 6px 0 0', background:bgs[i], display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', color:'#fff', padding:'10px 8px', textAlign:'center', boxShadow:i===1?`0 8px 24px ${color}40`:'none', position:'relative', overflow:'hidden' }}>
              <div style={{ fontFamily:'Space Mono,monospace', fontSize:22, fontWeight:700, lineHeight:1 }}>{r.totalPoints||0}</div>
              <div style={{ fontSize:9, letterSpacing:'.1em', textTransform:'uppercase', opacity:.75, marginTop:2 }}>pts</div>
              {r.formula && <div style={{ fontSize:9, opacity:.6, marginTop:3, fontFamily:'Space Mono,monospace', textTransform:'capitalize' }}>{r.formula}</div>}
            </div>
            <div style={{ width:'100%', padding:'6px 4px', background:'#F5F0E8', borderRadius:'0 0 4px 4px', textAlign:'center', fontFamily:'Space Mono,monospace', fontSize:9, color:'#8C7A6A', letterSpacing:'.06em', textTransform:'uppercase' }}>{label}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function LiveRanking({ territory }) {
  const { managesTerritoire } = useAuth();
  const [edition] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; });
  const [exportLoading, setExportLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('classement');
  const { rankings, liveTeams, isConnected, raceActive, lastUpdate, loading, refresh } = useLiveRanking(territory.id, edition);
  const sorted = [...rankings].sort((a, b) => (b.totalPoints||0) - (a.totalPoints||0));
  const canManage = managesTerritoire(territory.id);
  const color = territory.color || 'var(--radar-orange, #D94F00)';

  const handleExport = async () => {
    setExportLoading(true);
    try { await rankingsApi.exportCSV(territory.id, { edition }); }
    catch (err) { alert('Erreur: ' + err.message); }
    finally { setExportLoading(false); }
  };

  if (loading) return <div style={{ padding:'60px 0', textAlign:'center', color:'#8C7A6A' }}>Chargement…</div>;

  const tabBtn = (id, label) => (
    <button key={id} onClick={() => setActiveTab(id)} style={{ padding:'10px 18px', background:'none', border:'none', cursor:'pointer', fontFamily:'Space Mono,monospace', fontSize:11, letterSpacing:'.08em', textTransform:'uppercase', color:activeTab===id?color:'#8C7A6A', borderBottom:`2px solid ${activeTab===id?color:'transparent'}`, marginBottom:-1, transition:'all .2s' }}>{label}</button>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22, flexWrap:'wrap', gap:10 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <h2 style={{ fontSize:22, fontWeight:700 }}>Classement</h2>
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <LiveDot connected={isConnected} />
            <span style={{ fontSize:11, color:'#8C7A6A', fontFamily:'Space Mono,monospace' }}>{isConnected?'Temps réel':'Hors ligne'}</span>
            {lastUpdate && <span style={{ fontSize:10, color:'#C4A882', fontFamily:'Space Mono,monospace' }}>· {lastUpdate.toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'})}</span>}
          </div>
          {raceActive && <span style={{ padding:'2px 8px', background:'rgba(76,175,80,.12)', border:'1px solid rgba(76,175,80,.3)', borderRadius:2, fontSize:10, fontFamily:'Space Mono,monospace', color:'#2E7D32' }}>🏁 EN COURS</span>}
        </div>
        <div style={{ display:'flex', gap:6 }}>
          <button onClick={refresh} style={{ padding:'6px 11px', background:'none', border:'1px solid rgba(140,122,106,.3)', borderRadius:3, fontFamily:'Space Mono,monospace', fontSize:10, color:'#8C7A6A', cursor:'pointer' }}>↻</button>
          {canManage && <button onClick={handleExport} disabled={exportLoading} style={{ padding:'6px 12px', background:'none', border:'1px solid rgba(140,122,106,.3)', borderRadius:3, fontFamily:'Space Mono,monospace', fontSize:10, color:'#5A4A3C', cursor:'pointer' }}>{exportLoading?'…':'⬇ CSV'}</button>}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', marginBottom:24, borderBottom:'1px solid rgba(140,122,106,.15)' }}>
        {tabBtn('classement', `Classement (${sorted.length})`)}
        {tabBtn('live', `En course${liveTeams.length>0?` (${liveTeams.length})`:''}`)}
      </div>

      {/* CLASSEMENT — points-based */}
      {activeTab === 'classement' && (
        sorted.length === 0 ? (
          <div style={{ padding:'60px 20px', textAlign:'center', color:'#8C7A6A' }}>
            <div style={{ fontSize:40, marginBottom:12 }}>🏆</div>
            <p>Aucun résultat pour cette édition.</p>
          </div>
        ) : (
          <>
            <Podium rankings={sorted} color={color} />
            <div style={{ marginBottom:12 }}>
              <span style={{ padding:'2px 10px', background:`${color}12`, borderRadius:2, fontFamily:'Space Mono,monospace', fontSize:9, color, letterSpacing:'.08em' }}>● CLASSEMENT PAR POINTS</span>
            </div>
            <div style={{ background:'#fff', border:'1px solid rgba(140,122,106,.15)', borderRadius:6, overflow:'hidden' }}>
              <div style={{ display:'grid', gridTemplateColumns:'48px 1fr 110px 80px 90px 90px', padding:'11px 20px', background:'#F5F0E8', fontFamily:'Space Mono,monospace', fontSize:9, letterSpacing:'.08em', color:'#8C7A6A', textTransform:'uppercase' }}>
                <span>#</span><span>Équipe</span><span>Formule</span><span>Temps</span><span style={{textAlign:'right'}}>CP pts</span><span style={{textAlign:'right',color}}>Total</span>
              </div>
              {sorted.map((r, i) => (
                <div key={r._id||i} style={{ display:'grid', gridTemplateColumns:'48px 1fr 110px 80px 90px 90px', padding:'13px 20px', alignItems:'center', borderTop:'1px solid rgba(140,122,106,.07)', background:i===0?`${color}06`:i<3?`${color}03`:'#fff' }}
                  onMouseEnter={e=>e.currentTarget.style.background=`${color}09`}
                  onMouseLeave={e=>e.currentTarget.style.background=i===0?`${color}06`:i<3?`${color}03`:'#fff'}>
                  <div style={{ width:28, height:28, borderRadius:'50%', background:i<3?color:'transparent', border:i<3?'none':'1px solid rgba(140,122,106,.2)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <span style={{ fontFamily:'Space Mono,monospace', fontWeight:700, fontSize:11, color:i<3?'#fff':'#8C7A6A' }}>{i+1}</span>
                  </div>
                  <div>
                    <div style={{ fontWeight:700, fontSize:14, color:'#1A1208' }}>{r.teamName}</div>
                    <div style={{ fontSize:11, color:'#8C7A6A', marginTop:1 }}>{(r.members||[]).join(', ')}</div>
                  </div>
                  <span style={{ fontSize:11, color:'#8C7A6A', textTransform:'capitalize', fontFamily:'Space Mono,monospace' }}>{r.formula}</span>
                  <span style={{ fontFamily:'Space Mono,monospace', fontSize:11, color:'#5A4A3C' }}>{r.totalTimeDisplay||formatTime(r.totalTimeMs)}</span>
                  <span style={{ fontFamily:'Space Mono,monospace', fontSize:12, textAlign:'right', color:'#8C7A6A' }}>{r.checkpointPoints||0}</span>
                  <div style={{ textAlign:'right' }}>
                    <span style={{ fontFamily:'Space Mono,monospace', fontSize:16, fontWeight:700, color }}>{r.totalPoints||0}</span>
                    <span style={{ fontSize:9, color:'#8C7A6A', marginLeft:2 }}>pts</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )
      )}

      {/* LIVE tab */}
      {activeTab === 'live' && (
        !raceActive && liveTeams.length === 0 ? (
          <div style={{ padding:'60px 20px', textAlign:'center', color:'#8C7A6A' }}>
            <div style={{ fontSize:40, marginBottom:12 }}>🏁</div>
            <p>Aucune course en cours. Le suivi s'active quand l'organisateur lance la course.</p>
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {liveTeams.map((team, i) => (
              <div key={team._id||i} style={{ background:'#fff', border:'1px solid rgba(140,122,106,.2)', borderRadius:6, padding:'14px 20px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
                  <div>
                    <div style={{ fontWeight:700, fontSize:15, marginBottom:2 }}>{team.teamName}</div>
                    <div style={{ fontSize:12, color:'#8C7A6A' }}>{(team.members||[]).join(', ')}</div>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    {team.lastCheckpoint && <div style={{ fontSize:13, color, fontWeight:500, marginBottom:2 }}>📍 {team.lastCheckpoint}</div>}
                    {team.startTime && <div style={{ fontFamily:'Space Mono,monospace', fontSize:13, color:'#1A1208' }}>⏱ {formatTime(Date.now()-new Date(team.startTime).getTime())}</div>}
                  </div>
                </div>
                {team.splits?.length>0 && (
                  <div style={{ display:'flex', gap:5, marginTop:10, flexWrap:'wrap' }}>
                    {team.splits.sort((a,b)=>a.checkpointOrder-b.checkpointOrder).map((split,si)=>(
                      <div key={si} style={{ padding:'2px 7px', background:`${color}12`, borderRadius:2, fontSize:10, fontFamily:'Space Mono,monospace', color }}>CP{split.checkpointOrder}·{formatTime(split.timeMs)}</div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
